import { useEffect, useState } from 'react';
import LocationInterface from '@solvhealth/types/interfaces/Location/index';
import isEmpty from 'lodash/isEmpty';
import { useNearbyPremiumLocations } from '~/core/dapi/premiumInPerson';
import { getSlotsForTodayAndTomorrow } from '~/core/location/slots';
import { usePreloadSlots } from '~/components/CareTeam/hooks/useSlots';

export type AvailableDay = 'today' | 'tomorrow';

type Return = {
  /**
   * The nearest location with availability today. If there are no locations
   * with availability today,the nearest location with availability tomorrow.
   * If there are no locations with availability today or tomorrow, undefined.
   */
  mostConvenientLocation?: LocationInterface;

  /** The relative day of the next available slot (today or tomorrow) */
  availableDay?: AvailableDay;

  /** The next available slot */
  availableSlot?: number;

  /** Are we currently loading? */
  loading: boolean;
};

export const useMostConvenientInPersonPremiumLocation = (): Return => {
  const { data: nearbyPremiumInPersonResults } = useNearbyPremiumLocations();

  const [mostConvenientLocation, setMostConvenientLocation] = useState<
    LocationInterface | undefined
  >(undefined);
  const [availableDay, setAvailableDay] = useState<AvailableDay | undefined>(undefined);
  const [availableSlot, setAvailableSlot] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const slots = usePreloadSlots(nearbyPremiumInPersonResults?.map((l) => l.id) || [], {
    enabled: true,
  });

  useEffect(() => {
    // Look for nearest location with availability today
    for (const data of slots) {
      const locationWithSlots: LocationInterface | undefined = data?.data?.data;
      const { today } = getSlotsForTodayAndTomorrow(locationWithSlots);

      if (today.length > 0) {
        setAvailableDay('today');
        setAvailableSlot(today[0].appointmentDate);
        setMostConvenientLocation(
          nearbyPremiumInPersonResults?.find((location) => location.id === locationWithSlots?.id)
        );
        break;
      }
    }

    if (isEmpty(mostConvenientLocation)) {
      // Look for nearest location with availability tomorrow
      for (const data of slots) {
        const locationWithSlots: LocationInterface | undefined = data?.data?.data;
        const { tomorrow } = getSlotsForTodayAndTomorrow(locationWithSlots);

        if (tomorrow.length > 0) {
          setAvailableDay('tomorrow');
          setAvailableSlot(tomorrow[0].appointmentDate);
          setMostConvenientLocation(
            nearbyPremiumInPersonResults?.find((location) => location.id === locationWithSlots?.id)
          );
          break;
        }
      }
    }

    setLoading(false);
  }, [mostConvenientLocation, nearbyPremiumInPersonResults, slots]);

  return { mostConvenientLocation, availableDay, availableSlot, loading };
};
